/**
 * Canvas Fabric.js que renderiza uma página do documento + bboxes editáveis
 * com cores semânticas (E8.F4.T03 base, T05 estende interações).
 *
 * Cores por estado (E54.F7.T10 — a rampa de sinal do Design System, não mais o
 * amarelo/verde/vermelho saturados que sobreviveram inteiros à troca de paleta
 * da F1 porque nenhum guard olhava para o Fabric):
 *   pending  → âmbar    (`--sc-warn-500`) — sugerida, ainda sem decisão
 *   accepted → positivo (`--sc-pos-500`)  — perito confirmou
 *   rejected → negativo (`--sc-neg-500`)  — perito descartou
 *   manual   → grafite  (`--fg-dim`)      — bbox desenhada pelo perito, que não
 *                                           é resultado e por isso não é sinal
 *
 * Convenção de coordenadas: `bbox` em [0,1] (contrato backend), convertido
 * para pixels via `bboxToCanvas` no momento da montagem. Edits saem em
 * `canvasToBbox` (clamp em [0,1] já aplicado).
 *
 * Importante (espelha `AnalysisCanvas`): o canvas Fabric é a fonte da
 * verdade enquanto montado. Mudanças em `bboxes` são reconciliadas via
 * efeito separado (não re-montam o canvas inteiro pra evitar loop React
 * ↔ Fabric ou re-fetch do image blob). O `<div>` wrapper expõe
 * `data-bbox-count` e `data-page-number` para Playwright/Vitest.
 *
 * T05 estende este componente com: seleção/resize via controles, handler
 * `on:object:modified` → emite bbox atualizada via `onBoundingBoxChange`,
 * modo de desenho manual disparado por `drawMode=true` (mousedown/move/up
 * cria nova fabric.Rect e emite via `onManualPinDrawn`).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import * as fabric from 'fabric';

import { ptBR } from '@/i18n/pt-BR';
import { bboxToCanvas, canvasToBbox, type NormalizedBoundingBox } from '@/lib/bbox_coords';
import { SIGNAL_HEX, TOKEN_HEX } from '@/lib/design_tokens';

export type BoundingBoxState = 'pending' | 'accepted' | 'rejected' | 'manual';

export interface DocumentBoundingBox {
  /** Identificador estável: candidate id (cand-*) ou pin id local (pin-*). */
  id: string;
  bbox: NormalizedBoundingBox;
  state: BoundingBoxState;
  /** Rótulo numérico exibido no canvas (ex.: índice 1-based da região). */
  label: string;
}

export interface BoundingBoxEdit {
  id: string;
  bbox: NormalizedBoundingBox;
}

export interface ManualPinDrawn {
  bbox: NormalizedBoundingBox;
}

/** Helpers entregues ao overlay HTML ancorado às boxes (E23.F26.T02). */
export interface CanvasOverlayHelpers {
  /** Seleciona a box no Fabric (mostra os controles de resize) — usado pela
   *  tesoura para tornar o ajuste inline descobrível sem abrir modal. */
  selectBox: (id: string) => void;
}

export interface DocumentPageCanvasProps {
  imageUrl: string | null;
  pageNumber: number;
  bboxes: DocumentBoundingBox[];
  drawMode?: boolean;
  maxWidthPx?: number;
  maxHeightPx?: number;
  onBoundingBoxChange?: ((edit: BoundingBoxEdit) => void) | undefined;
  onManualPinDrawn?: ((pin: ManualPinDrawn) => void) | undefined;
  onBoundingBoxSelected?: ((id: string | null) => void) | undefined;
  /**
   * E23.F26.T04: quando `true`, candidates (exceto rejeitadas) ficam
   * selecionáveis/redimensionáveis no canvas — o ajuste de recorte passa a ser
   * inline (arraste/resize → `onBoundingBoxChange`), sem o modal antigo. Pins
   * manuais são sempre editáveis. Desligado durante o modo de desenho manual.
   */
  candidatesEditable?: boolean;
  /**
   * E23.F26.T02: render-prop do overlay de ações ancorado às boxes. Recebe as
   * bboxes (normalizadas [0,1]) e helpers; o resultado é posicionado por
   * PERCENTUAL dentro do wrapper do canvas (robusto a zoom/resize — T06). A
   * camada é `pointer-events-none`; cada controle reativa `pointer-events`.
   */
  renderOverlay?:
    | ((boxes: DocumentBoundingBox[], helpers: CanvasOverlayHelpers) => ReactNode)
    | undefined;
}

const DEFAULT_MAX_WIDTH = 720;
const DEFAULT_MAX_HEIGHT = 920;
const STROKE_WIDTH = 2;
const MANUAL_PIN_MIN_PIXELS = 4;

/** Preenchimento a 18% do traço — o mesmo véu de sempre, agora derivado do hex. */
const FILL_ALPHA = 0.18;

function veu(hex: string): string {
  const limpo = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(limpo.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${FILL_ALPHA})`;
}

const STATE_STYLES: Record<BoundingBoxState, { stroke: string; fill: string }> = {
  pending: { stroke: SIGNAL_HEX.warn, fill: veu(SIGNAL_HEX.warn) },
  accepted: { stroke: SIGNAL_HEX.pos, fill: veu(SIGNAL_HEX.pos) },
  rejected: { stroke: SIGNAL_HEX.neg, fill: veu(SIGNAL_HEX.neg) },
  manual: { stroke: TOKEN_HEX.fgDim, fill: veu(TOKEN_HEX.fgDim) },
};

function applyStyles(rect: fabric.Rect, state: BoundingBoxState): void {
  const style = STATE_STYLES[state];
  rect.set({
    stroke: style.stroke,
    fill: style.fill,
    strokeWidth: STROKE_WIDTH,
  });
}

function configureForEdit(rect: fabric.Rect, editable: boolean): void {
  // C04 (round 2): apenas pins manuais permanecem editáveis. Candidates
  // (state ∈ {pending, accepted, rejected}) ficam read-only no canvas porque
  // o contrato `ExtractionDecision` (apps/api/types/extraction.py) não aceita
  // bbox editada e o backend recorta com `cand.bbox` original — habilitar
  // edição induzia o perito a um falso "redesenhar". Para alterar a região
  // de uma candidate o fluxo é: rejeitar + adicionar marcação manual.
  if (!editable) {
    rect.set({
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      hoverCursor: 'default',
    });
    return;
  }
  // T05: edição preserva alinhamento axial (sem rotação) e impede flip
  // (lockScalingFlip evita inversão acidental); resize livre em todas as
  // bordas mantendo controles padrão. mtr (rotação) escondido.
  rect.set({
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    lockRotation: true,
    lockScalingFlip: true,
    hoverCursor: 'move',
  });
  if (typeof rect.setControlsVisibility === 'function') {
    rect.setControlsVisibility({ mtr: false });
  }
}

export function DocumentPageCanvas({
  imageUrl,
  pageNumber,
  bboxes,
  drawMode = false,
  maxWidthPx = DEFAULT_MAX_WIDTH,
  maxHeightPx = DEFAULT_MAX_HEIGHT,
  onBoundingBoxChange,
  onManualPinDrawn,
  onBoundingBoxSelected,
  candidatesEditable = false,
  renderOverlay,
}: DocumentPageCanvasProps) {
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);

  // Refs evitam re-mount do canvas a cada render: callbacks/estado leem o
  // valor mais recente sem reconstruir Fabric.
  const onChangeRef = useRef(onBoundingBoxChange);
  onChangeRef.current = onBoundingBoxChange;
  const onPinRef = useRef(onManualPinDrawn);
  onPinRef.current = onManualPinDrawn;
  const onSelectedRef = useRef(onBoundingBoxSelected);
  onSelectedRef.current = onBoundingBoxSelected;
  const drawModeRef = useRef(drawMode);
  drawModeRef.current = drawMode;

  // Estado da imagem carregada: dimensões do canvas + flag "ready". O
  // efeito de reconciliação (segundo useEffect abaixo) depende destes
  // para semear/atualizar bboxes — assim mudanças em `bboxes` (novos
  // pins manuais, accept/reject) refletem visualmente sem re-montar.
  const dimsRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const trackedRef = useRef<Map<string, fabric.Rect>>(new Map());
  const [canvasGen, setCanvasGen] = useState(0);

  useEffect(() => {
    if (canvasElRef.current === null || imageUrl === null) return undefined;
    const canvasEl = canvasElRef.current;
    const fabricCanvas = new fabric.Canvas(canvasEl, {
      selection: true,
      preserveObjectStacking: true,
      renderOnAddRemove: true,
      backgroundColor: TOKEN_HEX.paper,
    });
    fabricRef.current = fabricCanvas;

    let cancelled = false;
    let canvasWidthPx = 0;
    let canvasHeightPx = 0;

    void (async () => {
      try {
        const img = await fabric.FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' });
        if (cancelled || fabricRef.current === null) return;
        const naturalWidth = img.width ?? 1;
        const naturalHeight = img.height ?? 1;
        const scale = Math.min(maxWidthPx / naturalWidth, maxHeightPx / naturalHeight, 1);
        canvasWidthPx = naturalWidth * scale;
        canvasHeightPx = naturalHeight * scale;
        fabricCanvas.setDimensions({ width: canvasWidthPx, height: canvasHeightPx });
        img.set({
          left: 0,
          top: 0,
          scaleX: scale,
          scaleY: scale,
          selectable: false,
          evented: false,
        });
        fabricCanvas.add(img);
        fabricCanvas.sendObjectToBack(img);
        dimsRef.current = { w: canvasWidthPx, h: canvasHeightPx };
        // Dispara o efeito de reconciliação — ele faz o seed inicial das
        // bboxes (e os updates subsequentes).
        setCanvasGen((g) => g + 1);
        fabricCanvas.requestRenderAll();
      } catch {
        // Falha silenciosa: parent decide o que mostrar (canvasFailed).
      }
    })();

    function handleModified(opts: { target?: fabric.Object | null }) {
      const target = opts.target;
      if (target === null || target === undefined) return;
      const data = (target as fabric.Object & { data?: { id?: string } }).data;
      const id = data?.id;
      if (typeof id !== 'string') return;
      const left = target.left ?? 0;
      const top = target.top ?? 0;
      const width = (target.width ?? 0) * (target.scaleX ?? 1);
      const height = (target.height ?? 0) * (target.scaleY ?? 1);
      const nextBbox = canvasToBbox({ left, top, width, height }, canvasWidthPx, canvasHeightPx);
      onChangeRef.current?.({ id, bbox: nextBbox });
    }

    function handleSelectionChange() {
      const active = fabricCanvas.getActiveObject();
      if (active === null || active === undefined) {
        onSelectedRef.current?.(null);
        return;
      }
      const data = (active as fabric.Object & { data?: { id?: string } }).data;
      onSelectedRef.current?.(typeof data?.id === 'string' ? data.id : null);
    }

    // T05: modo de desenho manual — mousedown inicia, mousemove redimensiona,
    // mouseup commita (se passar do mínimo) e emite via onManualPinDrawn.
    let drawingRect: fabric.Rect | null = null;
    let drawingStart: { x: number; y: number } | null = null;

    function handleMouseDown(opts: { e: fabric.TPointerEvent }) {
      if (!drawModeRef.current) return;
      const pointer = fabricCanvas.getPointer(opts.e);
      drawingStart = { x: pointer.x, y: pointer.y };
      drawingRect = new fabric.Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
      });
      applyStyles(drawingRect, 'manual');
      drawingRect.set({ selectable: false, evented: false });
      fabricCanvas.add(drawingRect);
    }

    function handleMouseMove(opts: { e: fabric.TPointerEvent }) {
      if (drawingRect === null || drawingStart === null) return;
      const pointer = fabricCanvas.getPointer(opts.e);
      const left = Math.min(pointer.x, drawingStart.x);
      const top = Math.min(pointer.y, drawingStart.y);
      const width = Math.abs(pointer.x - drawingStart.x);
      const height = Math.abs(pointer.y - drawingStart.y);
      drawingRect.set({ left, top, width, height });
      fabricCanvas.requestRenderAll();
    }

    function handleMouseUp() {
      if (drawingRect === null || drawingStart === null) {
        drawingRect = null;
        drawingStart = null;
        return;
      }
      const width = drawingRect.width ?? 0;
      const height = drawingRect.height ?? 0;
      const tooSmall = width < MANUAL_PIN_MIN_PIXELS || height < MANUAL_PIN_MIN_PIXELS;
      if (tooSmall) {
        fabricCanvas.remove(drawingRect);
      } else {
        const bbox = canvasToBbox(
          {
            left: drawingRect.left ?? 0,
            top: drawingRect.top ?? 0,
            width,
            height,
          },
          canvasWidthPx,
          canvasHeightPx,
        );
        fabricCanvas.remove(drawingRect);
        onPinRef.current?.({ bbox });
      }
      drawingRect = null;
      drawingStart = null;
    }

    fabricCanvas.on('object:modified', handleModified);
    fabricCanvas.on('selection:created', handleSelectionChange);
    fabricCanvas.on('selection:updated', handleSelectionChange);
    fabricCanvas.on('selection:cleared', handleSelectionChange);
    fabricCanvas.on('mouse:down', handleMouseDown);
    fabricCanvas.on('mouse:move', handleMouseMove);
    fabricCanvas.on('mouse:up', handleMouseUp);

    return () => {
      cancelled = true;
      fabricRef.current = null;
      dimsRef.current = { w: 0, h: 0 };
      trackedRef.current = new Map();
      fabricCanvas.off('object:modified', handleModified);
      fabricCanvas.off('selection:created', handleSelectionChange);
      fabricCanvas.off('selection:updated', handleSelectionChange);
      fabricCanvas.off('selection:cleared', handleSelectionChange);
      fabricCanvas.off('mouse:down', handleMouseDown);
      fabricCanvas.off('mouse:move', handleMouseMove);
      fabricCanvas.off('mouse:up', handleMouseUp);
      fabricCanvas.dispose();
    };
  }, [imageUrl, maxWidthPx, maxHeightPx]);

  // Reconciliação reativa: quando `bboxes` muda (novo pin manual,
  // accept/reject vindo da sidebar, troca de página), diff contra os
  // rects tracked. Espera o canvas estar montado e a imagem carregada
  // (dimsRef populado via `canvasGen` bump no async loader).
  useEffect(() => {
    const fabricCanvas = fabricRef.current;
    if (fabricCanvas === null) return;
    const { w, h } = dimsRef.current;
    if (w === 0 || h === 0) return;
    const tracked = trackedRef.current;

    const incomingIds = new Set(bboxes.map((b) => b.id));

    // Remove rects cujos ids saíram (ex.: pin manual removido pela sidebar,
    // ou troca de página descarta bboxes da página anterior).
    for (const [id, rect] of tracked.entries()) {
      if (!incomingIds.has(id)) {
        fabricCanvas.remove(rect);
        tracked.delete(id);
      }
    }

    // E23.F26.T04: candidates (exceto rejeitadas) ficam editáveis quando
    // `candidatesEditable` — o ajuste de recorte é inline (sem modal). Pins
    // manuais são sempre editáveis. Rejeitadas nunca (ajustar o que se
    // descartou não faz sentido).
    const isEditable = (state: BoundingBoxState): boolean =>
      state === 'manual' || (candidatesEditable && state !== 'rejected');

    // Adiciona novos / restyleia existentes.
    for (const item of bboxes) {
      const existingRect = tracked.get(item.id);
      if (existingRect === undefined) {
        const px = bboxToCanvas(item.bbox, w, h);
        const rect = new fabric.Rect({
          left: px.left,
          top: px.top,
          width: px.width,
          height: px.height,
        });
        applyStyles(rect, item.state);
        configureForEdit(rect, isEditable(item.state));
        (rect as fabric.Rect & { data?: unknown }).data = { id: item.id, label: item.label };
        tracked.set(item.id, rect);
        fabricCanvas.add(rect);
      } else {
        applyStyles(existingRect, item.state);
        configureForEdit(existingRect, isEditable(item.state));
        (existingRect as fabric.Rect & { data?: unknown }).data = {
          id: item.id,
          label: item.label,
        };
      }
    }

    fabricCanvas.requestRenderAll();
  }, [bboxes, canvasGen, candidatesEditable]);

  // E23.F26.T02: seleciona programaticamente a box (mostra os controles de
  // resize) — a tesoura do overlay usa isto para tornar o ajuste descobrível.
  const selectBox = useCallback((id: string) => {
    const fabricCanvas = fabricRef.current;
    const rect = trackedRef.current.get(id);
    if (fabricCanvas === null || rect === undefined) return;
    fabricCanvas.setActiveObject(rect);
    fabricCanvas.requestRenderAll();
  }, []);

  const ariaLabel = ptBR.extraction.canvasAriaLabel.replace('{{page}}', String(pageNumber));

  return (
    <div
      data-testid="extraction-document-canvas"
      data-bbox-count={bboxes.length}
      data-page-number={pageNumber}
      data-draw-mode={drawMode ? 'true' : 'false'}
      role="img"
      aria-label={ariaLabel}
      className="relative inline-block max-w-full overflow-hidden border border-border bg-paper"
    >
      <canvas ref={canvasElRef} className="block max-w-full" />
      {/* E23.F26.T02: overlay de ações ancorado às boxes. `inset-0` cobre
          exatamente o canvas (wrapper inline-block). A camada não captura
          ponteiro (deixa o arraste/seleção chegarem ao Fabric); cada controle
          reativa `pointer-events` no BoxActionOverlay. Só monta quando a imagem
          carregou (canvasGen>0), senão o wrapper ainda tem tamanho zero. */}
      {renderOverlay && imageUrl !== null && canvasGen > 0 ? (
        <div
          data-testid="extraction-box-overlay-layer"
          className="pointer-events-none absolute inset-0"
        >
          {renderOverlay(bboxes, { selectBox })}
        </div>
      ) : null}
      {imageUrl === null ? (
        <span className="block px-4 py-6 text-body-sm text-fg-muted">
          {ptBR.extraction.canvasLoading}
        </span>
      ) : null}
    </div>
  );
}
